<template>
  <div class="row" v-if="canSearch">
    <div class="col-md-12 card">
      <div class="card-body row">
        <div class="col-sm-12">
          <div class="pull-right">
            <el-input type="search" clearable class="input-sm task-template-search" placeholder="Search Task Template" v-model="searchQuery" aria-controls="datatables" style="width:300px" />
            <el-select class="select select-filter"
                       name="departmentFilter"
                       size="large"
                       clearable
                       placeholder="Department"
                       v-model="departmentFilter">
              <el-option v-for="option in departments"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <button type="button" v-if="canEdit" @click="newTaskTemplate" class="btn btn-fill btn-info btn-new-task-template pull-right">
              New Task Template
            </button>
          </div>
        </div>
        <div class="col-sm-12">
          <div v-if="loading" style="text-align: center;">
            <img src="static/img/loading.gif"/>
          </div>
          <el-table class="table-striped wrap-whole-words indexed-table-column-width"
                    v-else
                    :data="queriedData"
                    border
                    :max-height="tableHeight(330)"
                    style="width: 100%">
            <el-table-column
                :width="this.constants.INDEX_COLUMN_WIDTHS.WIDE"
                align="center"
                label="#"
                type="index" />
            <el-table-column
              :min-width="240"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'name')"
              label="Name">
              <template v-slot="props">
                <span class="invisible">{{ props.row.id }}</span>
                <a href="#" @click.prevent="handleEdit(props.row)">{{ props.row.name }}</a>
              </template>
            </el-table-column>
            <el-table-column
              :min-width="240"
              sortable
              :sort-method="(a, b) => callStringSort(a, b, 'key')"
              label="Key">
              <template v-slot="props">
                {{ props.row.key }}
              </template>
            </el-table-column>
            <el-table-column
              label="Departments"
              min-width="200">
              <template v-slot="props">
                {{joinDepartmentList(props.row.departments)}}
              </template>
            </el-table-column>
            <el-table-column
              v-if="canEdit"
              width="110"
              fixed="right"
              label="Actions">
              <template v-slot="props">
                <p-button type="success" size="sm" icon class="btn-left">
                  <i class="fa fa-edit" @click="handleEdit(props.row)"></i>
                </p-button>
                <p-button type="danger" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  notifySuccess,
  notifyError,
  objectById,
  stringSort,
  messageForSafeDelete,
  getTableHeight
} from '@/utilities/utils'
import {mapGetters} from 'vuex'
import {OK} from "http-status-codes"
import PButton from '@/components/UIComponents/Button.vue'
import Constants from '@/utilities/constants'
import Permissions from '@/utilities/permissions'

export default {
  components: {
    PButton
  },
  computed: {
    ...mapGetters({
      taskTemplates: 'tasks/templates/results',
      allDepartments: 'departments/results',
      departments: 'departments/kvpsForSelect'
    }),
    tableData() {
      return this.taskTemplates
    },
    queriedData() {
      if (!this.searchQuery && !this.departmentFilter) {
        return this.tableData
      }
      return this.tableData
        .filter((row) => {
          let isIncluded = false
          if (this.searchQuery) {
            for (let key of this.propsToSearch) {
              if (row[key]) {
                let rowValue = row[key].toString().toLowerCase()
                if (rowValue.includes && rowValue.includes(this.searchQuery.toLowerCase())) {
                  isIncluded = true
                }
              }
            }
          }
          if (this.departmentFilter && this.departmentFilter.length > 0) {
            if (row.departments.includes(this.departmentFilter)) {
            isIncluded = true
          }
        }
        return isIncluded
      })
    },
    canSearch() {
      return this.$can('read', Permissions.TASK_TEMPLATES)
    },
    canEdit() {
      return this.$can('edit', Permissions.TASK_TEMPLATES)
    }
  },
  data() {
    return {
      toDelete: null,
      searchQuery: '',
      propsToSearch: ['name'],
      departmentFilter: [],
      constants: Constants,
      loading: false
    }
  },
  methods: {
    newTaskTemplate() {
      this.$store.set('tasks/templates/resetEdit')
      this.$eventHub.$emit('show-modal-1', 'Task Template', null, '1100px')
    },
    handleEdit(taskTemplate) {
      this.$eventHub.$emit('show-modal-1', 'Task Template', taskTemplate, '1100px')
    },
    callStringSort(a, b, prop) {
      return stringSort(a, b, prop)
    },
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    async confirmDelete(taskTemplate) {
      this.toDelete = taskTemplate.id
      let showButton = false
      const response = await this.$store.dispatch('tasks/fetchExistsByTemplateId', taskTemplate.id)
      if (OK !== response.status) {
        notifyError(this, 'Error determining if ' + taskTemplate.name + ' is in use. Unable to delete task template.')
        return
      }

      if (response.data === true) {
        message = '<strong>' + taskTemplate.name + '</strong> is used by at least one task and cannot be deleted. You may inactivate it.'
        showButton = false
      }

      let message = 'Are you sure you want to delete <strong>' + taskTemplate.name + '</strong>? This will completely remove the task template.'

      const responseTaskNames = await this.$store.dispatch('tasks/names/findByTaskTemplateId', taskTemplate.id)
      const usedByTaskNames = responseTaskNames.data
      if (OK === responseTaskNames.status && usedByTaskNames.length > 0) {
        message = '<strong>' + taskTemplate.name + '</strong> is used by the following task names and cannot be deleted:'
        message += '<p>'
        message = messageForSafeDelete(usedByTaskNames, 'name', message)
        message += '</p>'
        message += '<p>If you really want to delete this task template, you must first remove associations to it in each of the task names listed above, then come back here and delete the task template.</p>'
        showButton = false
        this.showConfirmDelete(taskTemplate, message, showButton)
        return
      }

      // happy path - task template can be deleted
      showButton = true
      this.showConfirmDelete(taskTemplate, message, showButton)
    },
    showConfirmDelete(taskTemplate, message, showButton) {
      const payload = {
        message,
        entity: taskTemplate,
        delete: this.delete,
        showButton,
      }
      this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
    },
    async delete() {
      const response = await this.$store.dispatch('tasks/templates/deleteById', this.toDelete)
      if (response.result === Constants.RETURN_CODES.SUCCESS) {
        notifySuccess(this, 'Task Template Deleted Successfully')
      } else {
        notifyError(this, response.message)
      }
    },
    joinDepartmentList(departmentIds) {
      const foundDepartments = []
      departmentIds.forEach((id) => {
        const department = objectById(this.allDepartments, id)
        if (department) {
          foundDepartments.push(department.name)
        }
      })
      return foundDepartments.join(', ')
    },
    async fetchAllTaskTemplates() {
      this.loading = true
      const ignoreLocalStorage = true
      await this.$store.dispatch('tasks/templates/fetchAll', ignoreLocalStorage)
      this.loading = false
    }
  },
  async beforeMount() {
    await Promise.all([
      this.$store.cache.dispatch('departments/fetchAll'),
      this.fetchAllTaskTemplates()
    ])
    this.$eventHub.$on('taskTemplateSaved', this.fetchAllTaskTemplates)
  },
  destroyed() {
    this.$eventHub.$off('taskTemplateSaved')
  }
}
</script>

<style scoped>
  .select-filter {
    margin-right: 12px;
    width: 185px;
  }
  .task-template-search {
    margin-right: 12px;
  }
  .btn-new-task-template {
    margin-top: 1px;
  }
</style>
